import { useState, useEffect, useCallback } from 'react';
import { API } from '@/App';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Heartbeat, Play, Gear, CalendarBlank, Clock,
  ArrowClockwise, CaretRight, Circle, TrendUp,
  CheckCircle, WarningCircle, XCircle, Lightning,
  ArrowsLeftRight, Eye
} from '@phosphor-icons/react';

const STATUS_COLORS = {
  healthy: 'kova-green', at_risk: 'kova-amber', critical: 'kova-red',
};
const RATING_COLORS = {
  green: 'kova-green', amber: 'kova-amber', red: 'kova-red',
};
const RATING_ICONS = {
  green: CheckCircle, amber: WarningCircle, red: XCircle,
};
const DIM_LABELS = {
  connections: 'Connections', jobs: 'Jobs', schema: 'Schema',
  critical_path: 'Critical Path', anomalies: 'Anomalies',
};

export default function HeatCheckPage() {
  const [view, setView] = useState('log');
  const [log, setLog] = useState([]);
  const [logTotal, setLogTotal] = useState(0);
  const [config, setConfig] = useState(null);
  const [trends, setTrends] = useState(null);
  const [selectedReport, setSelectedReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [showConfig, setShowConfig] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [logRes, configRes, trendsRes] = await Promise.all([
        fetch(`${API}/heatcheck/log?limit=20`, { credentials: 'include' }),
        fetch(`${API}/heatcheck/config`, { credentials: 'include' }),
        fetch(`${API}/heatcheck/trends`, { credentials: 'include' }),
      ]);
      if (logRes.ok) {
        const d = await logRes.json();
        setLog(d.reports || []);
        setLogTotal(d.total || 0);
      }
      if (configRes.ok) setConfig(await configRes.json());
      if (trendsRes.ok) setTrends(await trendsRes.json());
    } catch (e) {
      console.error('Heat Check fetch error:', e);
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleRun = async () => {
    setRunning(true);
    try {
      const res = await fetch(`${API}/heatcheck/run`, { method: 'POST', credentials: 'include' });
      if (res.ok) {
        const report = await res.json();
        setSelectedReport(report);
        setView('report');
        fetchData();
      }
    } catch (e) {
      console.error('Run error:', e);
    }
    setRunning(false);
  };

  const openReport = async (reportId) => {
    const res = await fetch(`${API}/heatcheck/log/${reportId}`, { credentials: 'include' });
    if (res.ok) {
      setSelectedReport(await res.json());
      setView('report');
    }
  };

  return (
    <div className="space-y-6" data-testid="heatcheck-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-kova-white tracking-tight">Weekly Heat Check</h1>
          <p className="font-mono text-[10px] tracking-[2px] uppercase text-kova-slate mt-1">
            F-012 AI Agent Health Assessment
            {config?.is_active && (
              <span className="ml-3 inline-block border border-kova-green/20 bg-kova-green/10 text-kova-green px-1.5 py-0.5">
                ACTIVE
              </span>
            )}
          </p>
        </div>
        <div className="flex gap-2">
          <button
            data-testid="config-btn"
            onClick={() => setShowConfig(true)}
            className="flex items-center gap-2 font-mono text-[10px] tracking-[2px] uppercase text-kova-silv border border-kova-cyan/12 px-3 py-2 hover:border-kova-cyan/25 hover:text-kova-white transition-colors"
          >
            <Gear size={14} />
            Configure
          </button>
          <button
            data-testid="run-heatcheck-btn"
            onClick={handleRun}
            disabled={running}
            className="flex items-center gap-2 font-mono text-[10px] tracking-[2px] uppercase bg-kova-cyan text-kova-bg px-4 py-2 hover:bg-kova-cyan2 transition-colors disabled:opacity-50 cursor-crosshair"
          >
            {running ? <ArrowClockwise size={14} className="animate-spin" /> : <Play size={14} weight="bold" />}
            {running ? 'Running...' : 'Run Now'}
          </button>
        </div>
      </div>

      {/* Trend Sparklines */}
      {trends && trends.report_count > 0 && (
        <div className="grid grid-cols-5 gap-px bg-kova-cyan/12">
          {Object.entries(DIM_LABELS).map(([key, label]) => {
            const dimTrend = trends.trends?.[key] || [];
            return (
              <div key={key} className="bg-kova-card p-3">
                <p className="font-mono text-[9px] tracking-[1px] uppercase text-kova-slate mb-2">{label}</p>
                <TrendBar data={dimTrend} />
              </div>
            );
          })}
        </div>
      )}

      {/* View tabs */}
      <div className="flex gap-0 border-b border-kova-cyan/12">
        {[
          { key: 'log', label: 'Health Log' },
          { key: 'report', label: 'Report Detail', disabled: !selectedReport },
        ].map(t => (
          <button
            key={t.key}
            data-testid={`tab-${t.key}`}
            onClick={() => !t.disabled && setView(t.key)}
            disabled={t.disabled}
            className={`px-4 py-2 font-mono text-[10px] tracking-[2px] uppercase transition-colors ${
              view === t.key ? 'text-kova-cyan border-b-2 border-kova-cyan' : 'text-kova-slate hover:text-kova-silv disabled:opacity-30'
            }`}
          >
            {t.label}
          </button>
        ))}
        <span className="ml-auto font-mono text-[9px] text-kova-slate self-center pr-2">
          {logTotal} report{logTotal !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Content */}
      {loading ? (
        <div className="text-center py-16 text-kova-slate font-mono text-xs">Loading health data...</div>
      ) : view === 'log' ? (
        <HealthLog log={log} onOpen={openReport} />
      ) : selectedReport ? (
        <ReportDetail report={selectedReport} onBack={() => setView('log')} />
      ) : null}

      {/* Config Modal */}
      <AnimatePresence>
        {showConfig && (
          <ConfigModal config={config} onClose={() => setShowConfig(false)} onSave={() => { setShowConfig(false); fetchData(); }} />
        )}
      </AnimatePresence>
    </div>
  );
}

/* ─── Trend sparkline bar ─── */
function TrendBar({ data }) {
  if (!data || data.length === 0) return <div className="h-4 bg-kova-card2" />;
  const last = data[data.length - 1];
  const colorClass = RATING_COLORS[last?.rating] || 'kova-slate';
  return (
    <div className="flex items-end gap-0.5 h-6">
      {data.slice(-12).map((d, i) => (
        <div
          key={i}
          className={`flex-1 bg-${RATING_COLORS[d.rating] || 'kova-slate'}`}
          style={{ height: `${d.value * 33}%`, minHeight: 2 }}
        />
      ))}
      <span className={`ml-2 font-mono text-[10px] font-bold text-${colorClass}`}>
        {last?.rating?.toUpperCase()}
      </span>
    </div>
  );
}

/* ─── Health Log Timeline ─── */
function HealthLog({ log, onOpen }) {
  if (log.length === 0) {
    return (
      <div className="text-center py-16 border border-kova-cyan/12 bg-kova-card">
        <Heartbeat size={32} className="mx-auto mb-3 text-kova-slate" />
        <p className="text-kova-silv text-sm">No heat check reports yet</p>
        <p className="text-kova-slate text-xs mt-1">Run your first assessment to establish a baseline</p>
      </div>
    );
  }

  return (
    <div className="space-y-1">
      {log.map((r) => {
        const statusColor = STATUS_COLORS[r.overall_status] || 'kova-slate';
        const StatusIcon = r.overall_status === 'healthy' ? CheckCircle : r.overall_status === 'at_risk' ? WarningCircle : XCircle;
        return (
          <motion.button
            key={r.report_id}
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            onClick={() => onOpen(r.report_id)}
            data-testid={`report-${r.report_id}`}
            className="w-full bg-kova-card border border-kova-cyan/12 p-4 hover:border-kova-cyan/25 transition-colors text-left group flex items-center gap-4"
          >
            <StatusIcon size={20} weight="fill" className={`text-${statusColor} flex-shrink-0`} />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className={`font-heading text-sm font-bold text-${statusColor}`}>
                  {r.overall_status?.replace('_', ' ').toUpperCase()}
                </span>
                <span className="font-mono text-[9px] text-kova-slate">
                  {r.run_type === 'on_demand' ? 'On-demand' : 'Scheduled'}
                </span>
              </div>
              <p className="text-kova-silv text-xs mt-0.5 truncate">{r.summary_text?.slice(0, 120)}...</p>
            </div>
            {/* Mini scorecard */}
            <div className="hidden md:flex gap-1 flex-shrink-0">
              {Object.entries(r.scorecard || {}).map(([dim, rating]) => (
                <div key={dim} className={`w-2 h-2 bg-${RATING_COLORS[rating] || 'kova-slate'}`} title={`${DIM_LABELS[dim]}: ${rating}`} />
              ))}
            </div>
            <div className="flex-shrink-0 text-right">
              <p className="font-mono text-[10px] text-kova-silv">
                {new Date(r.effective_date).toLocaleDateString()}
              </p>
              <p className="font-mono text-[9px] text-kova-slate">
                {new Date(r.effective_date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </p>
            </div>
            <CaretRight size={14} className="text-kova-slate group-hover:text-kova-cyan transition-colors flex-shrink-0" />
          </motion.button>
        );
      })}
    </div>
  );
}

/* ─── Full Report Detail ─── */
function ReportDetail({ report, onBack }) {
  const statusColor = STATUS_COLORS[report.overall_status] || 'kova-slate';
  const sections = report.sections || {};

  return (
    <div className="space-y-6">
      <button onClick={onBack} className="font-mono text-[10px] tracking-[2px] uppercase text-kova-silv hover:text-kova-white transition-colors">
        &larr; Back to Log
      </button>

      {/* Report header */}
      <div className="bg-kova-card border border-kova-cyan/12 p-6" data-testid="report-header">
        <div className="flex items-start justify-between">
          <div>
            <span className={`inline-block font-heading text-xl font-bold text-${statusColor}`}>
              {report.overall_status?.replace('_', ' ').toUpperCase()}
            </span>
            <p className="font-mono text-[10px] text-kova-slate mt-1">
              {new Date(report.effective_date).toLocaleString()} &middot; {report.run_type === 'on_demand' ? 'On-demand' : 'Scheduled'} &middot; v{report.report_version}
            </p>
          </div>
          <span className="font-mono text-[9px] text-kova-slate">{report.report_id}</span>
        </div>
      </div>

      {/* Executive Summary */}
      <div className="bg-kova-card border border-kova-cyan/12 p-5" data-testid="executive-summary">
        <h3 className="font-mono text-[9px] tracking-[2px] uppercase text-kova-slate mb-3">Executive Summary</h3>
        <p className="text-kova-white text-sm leading-relaxed">{sections.executive_summary}</p>
      </div>

      {/* Scorecard Grid */}
      <div data-testid="scorecard">
        <h3 className="font-mono text-[9px] tracking-[2px] uppercase text-kova-slate mb-3">Health Scorecard</h3>
        <div className="grid grid-cols-5 gap-px bg-kova-cyan/12">
          {Object.entries(DIM_LABELS).map(([dim, label]) => {
            const rating = report.scorecard?.[dim] || 'green';
            const rColor = RATING_COLORS[rating];
            const RIcon = RATING_ICONS[rating];
            const rationale = sections.health_scorecard?.dimensions?.[dim]?.rationale || '';
            return (
              <div key={dim} className="bg-kova-card p-4">
                <div className="flex items-center gap-2 mb-2">
                  <RIcon size={16} weight="fill" className={`text-${rColor}`} />
                  <span className={`font-heading text-sm font-bold text-${rColor}`}>{rating.toUpperCase()}</span>
                </div>
                <p className="font-mono text-[9px] tracking-[1px] uppercase text-kova-slate">{label}</p>
                <p className="text-kova-silv text-[11px] mt-1">{rationale}</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Connection Detail */}
      {sections.connection_detail && (
        <div data-testid="connection-detail">
          <h3 className="font-mono text-[9px] tracking-[2px] uppercase text-kova-slate mb-3">
            Connection Detail ({sections.connection_detail.total} connections)
          </h3>
          <div className="border border-kova-cyan/12">
            <div className="grid grid-cols-5 bg-kova-card2 px-4 py-2 font-mono text-[9px] tracking-[1px] uppercase text-kova-slate">
              <span>Connection</span><span>Status</span><span>Uptime 7d</span><span>Errors 7d</span><span>Runs 7d</span>
            </div>
            {(sections.connection_detail.details || []).map((c, i) => {
              const hColor = c.health_status === 'healthy' ? 'kova-green' : c.health_status === 'warning' ? 'kova-amber' : 'kova-red';
              return (
                <div key={i} className="grid grid-cols-5 px-4 py-2.5 border-t border-kova-cyan/6 font-mono text-xs">
                  <span className="text-kova-white truncate">{c.name}</span>
                  <span className={`text-${hColor} uppercase text-[10px]`}>{c.health_status}</span>
                  <span className="text-kova-silv">{c.uptime_7d}%</span>
                  <span className={c.error_count_7d > 0 ? 'text-kova-red' : 'text-kova-silv'}>{c.error_count_7d}</span>
                  <span className="text-kova-silv">{c.total_runs_7d}</span>
                </div>
              );
            })}
            {(!sections.connection_detail.details || sections.connection_detail.details.length === 0) && (
              <div className="px-4 py-6 text-center text-kova-slate font-mono text-xs">No connections configured</div>
            )}
          </div>
        </div>
      )}

      {/* Schema Changes */}
      {sections.schema_changes && (
        <div data-testid="schema-changes">
          <h3 className="font-mono text-[9px] tracking-[2px] uppercase text-kova-slate mb-3">
            Schema Changes ({sections.schema_changes.total_changes_7d} in 7 days)
          </h3>
          <div className="grid grid-cols-3 gap-px bg-kova-cyan/12">
            <div className="bg-kova-card p-4">
              <p className="font-heading text-xl font-bold text-kova-white">{sections.schema_changes.total_changes_7d}</p>
              <p className="font-mono text-[9px] text-kova-slate mt-1">Total Changes</p>
            </div>
            <div className="bg-kova-card p-4">
              <p className="font-heading text-xl font-bold text-kova-green">{sections.schema_changes.acknowledged}</p>
              <p className="font-mono text-[9px] text-kova-slate mt-1">Acknowledged</p>
            </div>
            <div className="bg-kova-card p-4">
              <p className={`font-heading text-xl font-bold ${sections.schema_changes.unacknowledged > 0 ? 'text-kova-red' : 'text-kova-silv'}`}>
                {sections.schema_changes.unacknowledged}
              </p>
              <p className="font-mono text-[9px] text-kova-slate mt-1">Unacknowledged</p>
            </div>
          </div>
        </div>
      )}

      {/* Anomaly Summary */}
      {sections.anomaly_summary && (
        <div data-testid="anomaly-summary">
          <h3 className="font-mono text-[9px] tracking-[2px] uppercase text-kova-slate mb-3">
            Anomaly Summary ({sections.anomaly_summary.total_7d} in 7 days)
          </h3>
          <div className="grid grid-cols-3 gap-px bg-kova-cyan/12">
            <div className="bg-kova-card p-4">
              <p className="font-heading text-xl font-bold text-kova-white">{sections.anomaly_summary.total_7d}</p>
              <p className="font-mono text-[9px] text-kova-slate mt-1">Total Anomalies</p>
            </div>
            <div className="bg-kova-card p-4">
              <p className="font-heading text-xl font-bold text-kova-green">{sections.anomaly_summary.resolved}</p>
              <p className="font-mono text-[9px] text-kova-slate mt-1">Resolved</p>
            </div>
            <div className="bg-kova-card p-4">
              <p className={`font-heading text-xl font-bold ${sections.anomaly_summary.active > 0 ? 'text-kova-amber' : 'text-kova-silv'}`}>
                {sections.anomaly_summary.active}
              </p>
              <p className="font-mono text-[9px] text-kova-slate mt-1">Active</p>
            </div>
          </div>
        </div>
      )}

      {/* Intelligence Benchmarks */}
      {sections.intelligence_benchmarks?.available && (
        <div className="bg-kova-card border border-kova-cyan/12 p-5" data-testid="intel-benchmarks">
          <h3 className="font-mono text-[9px] tracking-[2px] uppercase text-kova-slate mb-3">Intelligence Benchmarks</h3>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <p className="font-heading text-lg font-bold text-kova-cyan">{sections.intelligence_benchmarks.community_signals}</p>
              <p className="font-mono text-[9px] text-kova-slate">Community Signals</p>
            </div>
            <div>
              <p className="font-heading text-lg font-bold text-kova-white">{sections.intelligence_benchmarks.own_signals}</p>
              <p className="font-mono text-[9px] text-kova-slate">Your Signals</p>
            </div>
            <div>
              <p className="font-heading text-lg font-bold text-kova-purple">{sections.intelligence_benchmarks.commons_pool_size}</p>
              <p className="font-mono text-[9px] text-kova-slate">Commons Pool</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── Config Modal ─── */
function ConfigModal({ config, onClose, onSave }) {
  const [form, setForm] = useState({
    run_day: config?.run_day || 'monday',
    run_hour: config?.run_hour ?? 7,
    timezone: config?.timezone || 'UTC',
    is_active: config?.is_active ?? false,
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    const res = await fetch(`${API}/heatcheck/config`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(form),
    });
    setSaving(false);
    if (res.ok) onSave();
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95 }}
        animate={{ scale: 1 }}
        exit={{ scale: 0.95 }}
        className="bg-kova-card border border-kova-cyan/12 p-6 w-full max-w-md"
        onClick={e => e.stopPropagation()}
        data-testid="config-modal"
      >
        <h2 className="font-heading text-lg font-bold text-kova-white mb-4">Heat Check Schedule</h2>
        <form onSubmit={handleSave} className="space-y-4">
          <div className="flex items-center gap-3">
            <label className="font-mono text-[9px] tracking-[2px] uppercase text-kova-slate">Active</label>
            <button
              type="button"
              data-testid="config-active-toggle"
              onClick={() => setForm(p => ({ ...p, is_active: !p.is_active }))}
              className={`w-10 h-5 rounded-full transition-colors ${form.is_active ? 'bg-kova-cyan' : 'bg-kova-card2 border border-kova-cyan/12'}`}
            >
              <div className={`w-4 h-4 rounded-full bg-white transition-transform ${form.is_active ? 'translate-x-5' : 'translate-x-0.5'}`} />
            </button>
          </div>
          <div>
            <label className="font-mono text-[9px] tracking-[2px] uppercase text-kova-slate block mb-1">Run Day</label>
            <select
              data-testid="config-run-day"
              value={form.run_day}
              onChange={e => setForm(p => ({ ...p, run_day: e.target.value }))}
              className="w-full bg-kova-bg border border-kova-cyan/12 text-kova-white font-mono text-xs px-3 py-2"
            >
              {['monday','tuesday','wednesday','thursday','friday','saturday','sunday'].map(d => (
                <option key={d} value={d}>{d.charAt(0).toUpperCase() + d.slice(1)}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="font-mono text-[9px] tracking-[2px] uppercase text-kova-slate block mb-1">Run Hour (0-23)</label>
            <input
              data-testid="config-run-hour"
              type="number" min="0" max="23"
              value={form.run_hour}
              onChange={e => setForm(p => ({ ...p, run_hour: parseInt(e.target.value) || 0 }))}
              className="w-full bg-kova-bg border border-kova-cyan/12 text-kova-white font-mono text-xs px-3 py-2"
            />
          </div>
          <div>
            <label className="font-mono text-[9px] tracking-[2px] uppercase text-kova-slate block mb-1">Timezone</label>
            <select
              value={form.timezone}
              onChange={e => setForm(p => ({ ...p, timezone: e.target.value }))}
              className="w-full bg-kova-bg border border-kova-cyan/12 text-kova-white font-mono text-xs px-3 py-2"
            >
              {['UTC','America/New_York','America/Chicago','America/Denver','America/Los_Angeles','Europe/London','Europe/Berlin','Asia/Tokyo'].map(tz => (
                <option key={tz} value={tz}>{tz}</option>
              ))}
            </select>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="font-mono text-[10px] tracking-[2px] uppercase text-kova-silv border border-kova-cyan/12 px-4 py-2 hover:border-kova-cyan/25">Cancel</button>
            <button data-testid="config-save" type="submit" disabled={saving} className="font-mono text-[10px] tracking-[2px] uppercase bg-kova-cyan text-kova-bg px-4 py-2 hover:bg-kova-cyan2 disabled:opacity-50">
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
}
